<template>
  <div class="self_onboarding-special-field">
    <div v-html="specialFieldContent" class="mb-5" />

    <ValidationProvider
      name="selfOnboardingManagingDirectorFirstName"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{
            $t("mp:self_onboarding.input.managing_director_first_name.label")
          }}
          <b-tooltip
            type="is-dark"
            :label="
              $t(
                'mp:self_onboarding.input.managing_director_first_name.tooltip'
              )
            "
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input
          v-model="fields.managingDirectorFirstName"
          type="text"
          @blur="onBlur"
        />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingManagingDirectorLastName"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.managing_director_last_name.label") }}
          <b-tooltip
            type="is-dark"
            :label="
              $t('mp:self_onboarding.input.managing_director_last_name.tooltip')
            "
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input
          v-model="fields.managingDirectorLastName"
          type="text"
          @blur="onBlur"
        />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingBrandName"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.brand_name.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.brand_name.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input v-model="fields.brandName" type="text" @blur="onBlur" />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingWebsite"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.website.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.website.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input v-model="fields.website" type="text" @blur="onBlur" />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingFinancier"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0] || !isFinancierValid,
          'is-success':
            (validated && valid && required) || (isFinancierValid && validated),
        }"
        :message="
          !isFinancierValid
            ? $t('mp:self_onboarding.input.financier.not_unique_error')
            : errors
        "
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.financier.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.financier.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input
          v-model="fields.financier"
          type="text"
          @blur="onBlur"
          @input="onFinancierInput(validated)"
        />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingBankAccountNumber"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.bank_account_number.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.bank_account_number.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input
          v-model="fields.bankAccountNumber"
          type="text"
          @blur="onBlur"
        />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingAddress"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.address.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.address.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input v-model="fields.address" type="text" @blur="onBlur" />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingFacebookUrl"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.facebook_url.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.facebook_url.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input v-model="fields.facebookUrl" type="text" @blur="onBlur" />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingCategoryName"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.categories_ids.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.categories_ids.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-select v-model="fields.categoryName" @input="onBlur" expanded>
          <option
            v-for="category in filteredMerchantCategories"
            :key="category.value"
            :value="category.name"
          >
            {{ category.name }}
          </option>
        </b-select>
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingCompanyRegistrationNumber"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.company_registration_number.label") }}
          <b-tooltip
            type="is-dark"
            :label="
              $t('mp:self_onboarding.input.company_registration_number.tooltip')
            "
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input
          v-model="fields.companyRegistrationNumber"
          type="text"
          @blur="onBlur"
        />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingSeat"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.seat.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.seat.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input v-model="fields.seat" type="text" @blur="onBlur" />
      </b-field>
    </ValidationProvider>

    <ValidationProvider
      name="selfOnboardingOfferName"
      :rules="rules"
      v-slot="{ errors, validated, valid }"
    >
      <b-field
        class="validator-field"
        :type="{
          'is-danger': errors[0],
          'is-success': validated && valid && required,
        }"
        :message="errors"
      >
        <template #label>
          {{ $t("mp:self_onboarding.input.offer_name.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.input.offer_name.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-input v-model="fields.offerName" type="text" @blur="onBlur" />
      </b-field>
    </ValidationProvider>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  name: "SelfOnboardingSpecialField",
  props: {
    specialFieldContent: {
      type: String,
      required: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loaded: false,
      applicationVisiblePoll: null,
      isPurchaseListEmpty: true,
      isFinancierValid: false,
      fields: {
        managingDirectorFirstName: null, // private String
        managingDirectorLastName: null, // private String
        onboardingConfigId: null, // private Long (set in created)
        brandName: null, // private String
        bankAccountNumber: null, // private String
        address: null, // private String
        website: null, // private String
        facebookUrl: null, // private String
        categoryName: null, // private String
        companyRegistrationNumber: null, // private String
        seat: null, // private String
        // isLimit: null, // private Boolean
        // limitExpiryInDays: null, // private Integer
        // barionWalletEmailAddress: null, // private String
        // barionPosKey: null, // private String
        offerName: null, // private String
        financier: null, // private String
      },
    };
  },
  watch: {
    fields: {
      async handler(fields) {
        let isValid = true;
        // eslint-disable-next-line unused-imports/no-unused-vars
        for (const [key, value] of Object.entries(fields)) {
          // console.log(value);
          if (!value) {
            isValid = false;
          }
        }

        this.setOnboardingDataValidity(isValid && this.isFinancierValid);
      },
      deep: true,
    },
    "fields.website": {
      async handler(website) {
        console.log(`website changed to ${website}`);
        const regex = /^(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-_]*)(?:\..+)$/;
        if (website) {
          if (regex.test(website)) {
            this.fields.financier = this.fields.website.replace(regex, "$1");
            // this.fields.financier.replaceAll(/ /g, "");
            await this.validateFinancier(this.fields.financier);
            this.setOnboardingDataValidity(
              this.getOnboardingDataValidity && this.isFinancierValid
            );
          }
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      flowConfig: "application/flowConfig",
      onboardingData: "onboarding/onboardingData",
      merchantCategories: "onboarding/merchantCategories",
      getOnboardingDataValidity: "onboarding/getOnboardingDataValidity",
    }),
    filteredMerchantCategories() {
      return this.merchantCategories.filter(
        (category) => !["BNPL", "POS"].includes(category.name)
      );
    },
    rules() {
      if (this.required) {
        return "required";
      }
      return "";
    },
  },
  methods: {
    ...mapActions({
      getMyData: "onboarding/getMyData",
      fetchMerchantCategories: "onboarding/fetchMerchantCategories",
      putOnboardingData: "onboarding/putOnboardingData",
      doesFinancierExist: "onboarding/doesFinancierExist",
    }),
    ...mapMutations({
      setOnboardingDataValidity: "onboarding/setOnboardingDataValidity",
    }),
    async validateFinancier(financier) {
      const isValid = !(await this.doesFinancierExist(financier));
      this.isFinancierValid = isValid;
    },
    async onBlur() {
      this.putOnboardingData(this.fields);
    },
    async onFinancierInput(validated) {
      if (validated) {
        await this.validateFinancier(this.fields.financier);
        this.setOnboardingDataValidity(
          this.getOnboardingDataValidity && this.isFinancierValid
        );
      }
    },
  },
  async created() {
    this.fields.onboardingConfigId = this.flowConfig.id;
    this.fetchMerchantCategories();
    await this.getMyData();
    this.fields = { ...this.fields, ...this.onboardingData };
    if (!Object.entries(this.onboardingData).length) {
      this.putOnboardingData(this.fields);
    }
    let isValid = true;
    // eslint-disable-next-line unused-imports/no-unused-vars
    for (const [key, value] of Object.entries(this.fields)) {
      if (!value) {
        isValid = false;
        console.log(`Field ${key} is empty`);
      }
    }

    await this.validateFinancier(this.fields.financier);
    this.setOnboardingDataValidity(isValid && this.isFinancierValid);
    this.loaded = true;
  },
};
</script>

<style lang="scss">
.self_onboarding-special-field {
  & .field {
    margin-bottom: 2rem;

    .label {
      margin-bottom: 4px;
      display: flex;
      gap: 4px;
      align-items: center;

      .tooltip-trigger {
        height: 16px;
      }

      .b-tooltip {
        height: 16px;
      }
    }
  }
}
</style>
